export const QueryKeys = {
  AllVendorsList: "AllVendorsList",
  ThirdPartyVendorToken: "ThirdPartyVendorToken",
  AllFrameworksList: "AllFrameworksList",
  ControlSets: "ControlSets",
  StytchMembersList: "StytchMembersList",
  SessionDetails: "SessionDetails",
  Questionnaire: "Questionnaire",
  TPRASummary: "TPRASummary",
  TPRAVendorsList: "TPRAVendorsList", // For TPRA Dashboard vendors API
  TPRABreachHistoryList: "TPRABreachHistoryList", // For TPRA Dashboard breach history API
  TPRARisksList: "TPRARisksList", // For TPRA Dashboard risks API
  TPRASessions: "TPRASessions", // For TPRA Dashboard sessions API
  // TPRA Home Dashboard query keys
  TPRAHomeSummary: "TPRAHomeSummary",
  TPRAHomeAssessmentStages: "TPRAHomeAssessmentStages",
  TPRAHomeRiskHeatmap: "TPRAHomeRiskHeatmap",
  TPRAHomeFindingsSummary: "TPRAHomeFindingsSummary",
  TPRAHomeTasks: "TPRAHomeTasks",
  TPRAHomeRecentEvents: "TPRAHomeRecentEvents",
  TPRAAssessments: "TPRAAssessments",
  TierMappings: "TierMappings",
  WhitelistedDomains: "WhitelistedDomains",
};

/**
 * Query key factory for TPRA Vendors
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: tpraVendorsQueryKeys.list({ page: 1, page_size: 50 })
 */
export const tpraVendorsQueryKeys = {
  all: [QueryKeys.TPRAVendorsList] as const,
  lists: () => [...tpraVendorsQueryKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...tpraVendorsQueryKeys.lists(), filters] as const,
};

/**
 * Query key factory for TPRA Breach History
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: breachHistoryQueryKeys.list({ page: 1, page_size: 20 })
 */
export const breachHistoryQueryKeys = {
  all: [QueryKeys.TPRABreachHistoryList] as const,
  lists: () => [...breachHistoryQueryKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...breachHistoryQueryKeys.lists(), filters] as const,
};

/**
 * Query key factory for TPRA Risks
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: risksQueryKeys.list({ page: 1, page_size: 25 })
 */
export const risksQueryKeys = {
  all: [QueryKeys.TPRARisksList] as const,
  lists: () => [...risksQueryKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...risksQueryKeys.lists(), filters] as const,
};

/**
 * Query key factory for TPRA Home Dashboard
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: tpraHomeQueryKeys.summary()
 *   queryKey: tpraHomeQueryKeys.tasks()
 */
export const tpraHomeQueryKeys = {
  all: ["tpra-home"] as const,
  summary: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeSummary] as const,
  stages: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeAssessmentStages] as const,
  heatmap: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeRiskHeatmap] as const,
  findings: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeFindingsSummary] as const,
  tasks: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeTasks] as const,
  events: () => [...tpraHomeQueryKeys.all, QueryKeys.TPRAHomeRecentEvents] as const,
};

/**
 * Query key factory for TPRA Assessments
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: tpraAssessmentsQueryKeys.list({ page: 1, page_size: 25 })
 */
export const tpraAssessmentsQueryKeys = {
  all: [QueryKeys.TPRAAssessments] as const,
  lists: () => [...tpraAssessmentsQueryKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...tpraAssessmentsQueryKeys.lists(), filters] as const,
};

/**
 * Query key factory for Whitelisted Domains
 * Provides type-safe, structured query keys for TanStack Query caching
 *
 * Usage:
 *   queryKey: whitelistDomainQueryKeys.all
 */
/**
 * Query key factory for Tier Mappings
 */
export const tierMappingsQueryKeys = {
  all: [QueryKeys.TierMappings] as const,
};

export const whitelistDomainQueryKeys = {
  all: [QueryKeys.WhitelistedDomains] as const,
};
